import { retrieveBrowserLocale } from "@/Utils/i18n";
import type { IAddress } from "@medoma/models/addresses";
import {
  makePostalCodeMapFriendly,
  type InternalFormatPostalCode,
} from "@medoma/models/postalCode";
import type { SiteId } from "@medoma/models/sites";
import { z } from "zod";
import { mapboxApi } from "./ApiClient";
import { generateQueryString } from "./Helpers";
import { fetchCareHub } from "./Patients";

export const geocodingSchema = z.object({
  features: z.array(
    z.object({
      geometry: z.object({
        coordinates: z.tuple([z.number(), z.number()]),
      }),
    }),
  ),
});

export async function fetchCoordinates(
  parameters: Pick<
    IAddress<InternalFormatPostalCode[keyof InternalFormatPostalCode]>,
    "addressLine1" | "postalCode"
  > & {
    countryCode: "SE" | "CH" | "LV";
    siteId: SiteId;
  },
): Promise<{
  longitude: number;
  latitude: number;
}> {
  const careHub = await fetchCareHub(parameters.siteId);

  const { addressLine1: address_line1, postalCode, countryCode } = parameters;
  const postcode = makePostalCodeMapFriendly(postalCode, countryCode);

  // Ignored since Mapbox often thinks that the 'place' is the municipality, not the city.
  // const place = city;

  // Ignored since 'proximity' is more dynamic.
  // ~ 50 km north, west, east, south of careHub
  // const { minLon, minLat, maxLon, maxLat } = {
  //   minLon: careHub.longitude - 1,
  //   minLat: careHub.latitude - 0.5,
  //   maxLon: careHub.longitude + 1,
  //   maxLat: careHub.latitude + 0.5,
  // };
  // const bbox = `${minLon},${minLat},${maxLon},${maxLat}`;
  const country = countryCode.toLowerCase();
  const language = retrieveBrowserLocale();
  const limit = 1;
  const proximity = `${careHub.longitude},${careHub.latitude}`;
  const types = "address";
  const queryString = generateQueryString({
    address_line1,
    postcode,
    // bbox,
    country,
    language,
    limit,
    // place,
    proximity,
    types,
  });

  // https://docs.mapbox.com/api/search/geocoding/#forward-geocoding-with-structured-input
  const forwardGeocodingURL = `/search/geocode/v6/forward${queryString}`;
  const response = await mapboxApi
    .get(forwardGeocodingURL)
    .json(geocodingSchema);
  // Expected response as per: https://docs.mapbox.com/api/search/geocoding/#geocoding-response-object
  const feature = response.features[0];
  if (!feature) {
    throw new Error("No geocoding result found for the given address");
  }
  const coordinates: [number, number] = feature.geometry.coordinates;
  return { longitude: coordinates[0], latitude: coordinates[1] };
}
